import lodashGet from 'lodash/fp/get';
import type { UseFormSetError } from 'react-hook-form';
import { ValidationError, type ErrorDetails, type RewordedErrorDetails } from '#api/errors.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import type { HostKind } from '#util/host.ts';
import type { HostFormData } from './host-form-schema.ts';

export type IpAddressIndices = {
  /** Index of the IPv4 address in the sent `ipAddresses` array, or -1 if not sent. */
  v4Index: number;
  /** Index of the IPv6 address in the sent `ipAddresses` array, or -1 if not sent. */
  v6Index: number;
};

export function makeMapHostErrorsToForm(
  setError: UseFormSetError<HostFormData>,
  kind: HostKind,
  ipAddressIndices: IpAddressIndices = { v4Index: -1, v6Index: -1 }
) {
  return function mapHostErrorsToForm(error: ErrorDetails | RewordedErrorDetails) {
    const { path, message } = error;

    // Route indexed `ipAddresses[N]` errors by matching N to the index we sent
    // v4/v6 at — the server reports the position in our request array, not a
    // fixed slot, so v6-only requests emit `ipAddresses[0]` for a v6 error.
    const ipMatch = path?.match(/^ipAddresses\[(\d+)\]$/);
    if (ipMatch) {
      const idx = Number(ipMatch[1]);
      if (idx === ipAddressIndices.v4Index) {
        setError('ipv4Address', { message }, { shouldFocus: true });
        return true;
      }
      if (idx === ipAddressIndices.v6Index) {
        setError('ipv6Address', { message }, { shouldFocus: true });
        return true;
      }
    }

    switch (path) {
      case 'name':
      case 'roleID':
      case 'roleName':
      case 'listenPort':
      case 'ipv4Address':
      case 'ipv6Address':
      case 'tags':
      case 'configOverrides.definednet.dns.resolver_addrs':
      case 'configOverrides.definednet.dns.match_domains':
      case 'configOverrides.definednet.dns.search_domains':
      case 'configOverrides.routines':
      case 'configOverrides.preferred_ranges':
      case 'configOverrides.lighthouse.serve_dns':
      case 'configOverrides.lighthouse.dns.host':
      case 'configOverrides.lighthouse.dns.port':
      case 'configOverrides.lighthouse.advertise_addrs':
      case 'configOverrides.lighthouse.interval':
      case 'configOverrides.listen.host':
      case 'configOverrides.listen.batch':
      case 'configOverrides.listen.read_buffer':
      case 'configOverrides.listen.write_buffer':
      case 'configOverrides.listen.udp_offloads':
      case 'configOverrides.tun.tx_queue':
      case 'configOverrides.tun.use_offloads':
      case 'configOverrides.tun.dev':
      case 'configOverrides.tun.mtu':
      case 'configOverrides.tun.disabled':
      case 'configOverrides.logging.format':
      case 'configOverrides.logging.level':
      case 'configOverrides.logging.timestamp_format':
      case 'configOverrides.stats.type':
      case 'configOverrides.stats.host':
      case 'configOverrides.stats.prefix':
      case 'configOverrides.stats.namespace':
      case 'configOverrides.stats.subsystem':
      case 'configOverrides.stats.listen':
      case 'configOverrides.stats.path':
      case 'configOverrides.stats.interval':
      case 'configOverrides.stats.lighthouse_metrics':
      case 'configOverrides.stats.message_metrics':
      case 'configOverrides.static_map.network':
      case 'configOverrides.punchy.delay':
      case 'configOverrides.punchy.respond_delay':
        setError(path, { message }, { shouldFocus: true });
        return true;
      case 'configOverrides.lighthouse.local_allow_list':
      case 'configOverrides.lighthouse.remote_allow_list':
        // We focus these manually.
        setError(path, { message });
        return true;
      case 'staticAddresses[0]':
        // publicIP maps to staticAddresses, but only lighthouses render that field —
        // for other kinds the error must surface globally or it's invisible.
        if (kind === 'lighthouse') {
          setError('publicIPOrHostname', { message }, { shouldFocus: true });
        } else {
          setError('root.global', { message });
        }
        return true;
      case 'root.global':
      case undefined:
        setError('root.global', { message });
        return true;
      default:
        setError('root.global', { message });
        return false;
    }
  };
}

export function makeMapRoleErrorsToForm(setError: UseFormSetError<HostFormData>) {
  return function mapServerErrorsToForm(error: ErrorDetails | RewordedErrorDetails) {
    const { path, message } = error;
    switch (path) {
      case 'name':
        setError('roleName', { message }, { shouldFocus: true });
        return true;
      case 'root.global':
      case undefined:
        setError('root.global', { message });
        return true;
      default:
        return false;
    }
  };
}

export function mapListErrorsToForm(configOverrides: ConfigOverride[], error: ValidationError) {
  const remappedConfigOverrideErrors = error.errors
    .map((e) => {
      if (e.path?.includes('configOverrides')) {
        return { ...e, path: mapConfigOverrideApiErrorPathToForm(configOverrides, e.path) };
      }

      if (e.path?.includes('tags')) {
        // We only show one tag error, but you will see more as you fix previous ones.
        return { ...e, path: 'tags' };
      }

      return e;
    })
    .reverse(); // Reverse so that we trigger first error last, keeping the focus there.

  return new ValidationError(remappedConfigOverrideErrors, error.response);
}

function mapConfigOverrideApiErrorPathToForm(configOverrides: ConfigOverride[], path?: string) {
  if (!path) {
    return;
  }

  // We expect something like `configOverrides[${idx}].key`, so let's remove key/value from the end
  const overridePath = path.replace(/\.(key|value)/, '');
  const override = lodashGet(overridePath, { configOverrides }) as ConfigOverride | undefined;
  // An unresolvable path (index we never sent, unexpected shape) falls back to
  // `undefined`, which the host error mapper routes to a form-level message.
  return override ? `configOverrides.${override.key}` : undefined;
}
